import React, { useState, useEffect } from 'react';
import { Users, Heart, HandHeart, Award, ArrowRight, Star, ChevronRight, Sparkles, Clock, Target, Book } from 'lucide-react';
import { Link } from 'react-router-dom';

const ValueCard = ({ icon: Icon, title, description, index }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), index * 200);
        return () => clearTimeout(timer);
    }, [index]);

    return (
        <div className={`transform transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
            <div className="relative group cursor-pointer">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-purple-400/20
                      rounded-2xl transform transition-all duration-500
                      group-hover:scale-105 blur-xl opacity-0 group-hover:opacity-100" />
                <div className="bg-white p-8 rounded-2xl shadow-lg transform transition-all duration-500
                     relative overflow-hidden border border-purple-100/50 group-hover:border-purple-300
                     group-hover:shadow-purple-200/50">
                    <div className="absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br from-purple-100
                       to-purple-50/50 rounded-full group-hover:scale-150 transition-transform duration-700"/>
                    <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-gradient-to-tl from-purple-50
                       to-purple-100/50 rounded-full group-hover:scale-150 transition-transform duration-700"/>

                    <div className="relative z-10">
                        <div className="relative mb-6 inline-block">
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-500
                           rounded-xl transform rotate-3 group-hover:rotate-6 transition-all duration-500
                           blur-[2px] opacity-75" />
                            <div className="relative p-4 bg-gradient-to-r from-purple-600 to-purple-500 rounded-xl
                           transform rotate-3 group-hover:rotate-6 transition-all duration-500
                           group-hover:scale-110">
                                <Icon className="h-7 w-7 text-white transform -rotate-3 group-hover:-rotate-6
                             transition-all duration-500" />
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-purple-900 to-purple-600
                        bg-clip-text text-transparent group-hover:from-purple-700
                        group-hover:to-purple-500 transition-all duration-300">{title}</h3>
                        <p className="text-gray-600 leading-relaxed group-hover:text-gray-700
                       transition-colors duration-300">{description}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const HighlightCard = ({ icon: Icon, text }) => (
    <div className="group relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-purple-600
                  rounded-xl blur-lg opacity-0 group-hover:opacity-30 transition-all duration-500" />
        <div className="bg-white/10 p-6 rounded-xl backdrop-blur-md hover:bg-white/15
                  transition-all duration-500 relative border border-white/20
                  group-hover:border-white/30 group-hover:scale-105 transform">
            <div className="relative">
                <div className="absolute -inset-1 bg-white/20 rounded-lg blur-sm opacity-0
                     group-hover:opacity-100 transition-opacity duration-500" />
                <Icon className="h-6 w-6 text-purple-200 mb-4 relative z-10 group-hover:scale-110
                      group-hover:rotate-12 transform transition-all duration-500" />
            </div>
            <p className="text-white/90 group-hover:text-white transition-colors duration-300
                  relative z-10">{text}</p>
        </div>
    </div>
);

const ProgramCard = ({ image, title, description }) => (
    <div className="relative rounded-3xl overflow-hidden shadow-xl group">
        <div className="h-[400px] lg:h-[500px]">
            <img
                src={image}
                alt={title}
                className="w-full h-full object-cover transform transition-transform
                         duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90
                          via-gray-800/50 to-transparent opacity-80" />
            <div className="absolute inset-0 p-8 flex flex-col justify-end">
                <h3 className="text-2xl font-bold text-white mb-4">{title}</h3>
                <p className="text-gray-100 text-lg leading-relaxed">{description}</p>
            </div>
        </div>
    </div>
);

const AboutPage = () => {
    const values = [
        {
            icon: Heart,
            title: "Compassionate Care",
            description: "Providing holistic support with genuine love and understanding to those in need."
        },
        {
            icon: Users,
            title: "Community Unity",
            description: "Building stronger bonds through collective action, faith, and mutual support."
        },
        {
            icon: HandHeart,
            title: "Dedicated Service",
            description: "Committed to making a positive impact in people's lives through various support programs."
        },
        {
            icon: Award,
            title: "Excellence",
            description: "Striving for the highest standards in all our community initiatives and support programs."
        }
    ];

    return (
        <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-purple-50">
            {/* Hero Section */}
            <section className="relative min-h-[80vh] flex items-center overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-black/40" />
                    <img
                        src="/images/charity3.jpg"
                        alt="Community Impact"
                        className="w-full h-full object-cover object-center scale-105
                     motion-safe:animate-[ken-burns_20s_ease-in-out_infinite_alternate]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-purple-900/90
                       via-purple-900/50 to-black/30" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <div className="max-w-3xl mx-auto text-center space-y-8">
                        <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10
                         backdrop-blur-md border border-white/20 group hover:bg-white/15
                         transition-all duration-500">
                            <Heart className="h-5 w-5 text-purple-300 mr-2 group-hover:scale-110
                            group-hover:text-purple-200 transition-all duration-300" />
                            <span className="text-purple-100 font-medium">Serving Since 2008</span>
                        </div>
                        <h1 className="text-6xl md:text-7xl font-bold text-white leading-tight tracking-tight">
                            Building a Better
                            <div className="relative inline-block mt-2">
                                <span className="absolute -inset-1 bg-gradient-to-r from-purple-600/50
                                              to-purple-300/50 blur-xl opacity-50"></span>
                                <span className="relative block bg-gradient-to-r from-purple-200 to-white
                                              text-transparent bg-clip-text">
                                    Community Together
                                </span>
                            </div>
                        </h1>
                    </div>
                </div>
            </section>

            {/* Story Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Mission and Vision */}
                    <div className="bg-white rounded-3xl p-8 md:p-12 mb-20 shadow-xl border border-purple-100/20
                       transform hover:scale-[1.02] transition-transform duration-500">
                        <div className="max-w-3xl mx-auto">
                            <div className="flex items-center justify-center mb-8 space-x-4">
                                <div className="h-px w-12 bg-gradient-to-r from-transparent to-purple-300"></div>
                                <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-900 to-purple-600
                            bg-clip-text text-transparent">Our Journey</h2>
                                <div className="h-px w-12 bg-gradient-to-l from-transparent to-purple-300"></div>
                            </div>
                            <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                                <p className="leading-relaxed">
                                    The Community Charitable Ministry (CCM) is a non-profit organization established in
                                    December 2008 that has positively impacted lives and assisted with contributing to
                                    the reduction/elimination of homelessness, hunger, and the stress of families dealing
                                    with chronic illnesses of loved ones.
                                </p>
                                <p className="leading-relaxed">
                                    Since our inception, we have distributed monthly grocery packages and vouchers to many
                                    needy families, assisted with medical bills, and provided vital support to our community.
                                    Our community outreach programs provide holistic support to women, children, and families.
                                </p>
                                <p className="leading-relaxed">
                                    In June 2020, we launched the Antigua and Barbuda Cancer Support Community (ABCSC),
                                    initiated by Emelda Benjamin, one of our Executive members and a Cancer Survivor.
                                    This vital program provides medical assistance and monetary donations to cancer survivors
                                    and their families.
                                </p>
                                <p className="leading-relaxed">
                                    Recently, we've developed the Antigua and Barbuda Youth Entrepreneurship Training &
                                    Support (ABYETS) program for young persons between the ages 8-17, helping them become
                                    healthy, resourceful, and self-sufficient young businesspersons in Antigua and Barbuda.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Values Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
                        {values.map((value, index) => (
                            <ValueCard key={index} {...value} index={index} />
                        ))}
                    </div>

                    {/* Programs Section */}
                    <div className="mb-20">
                        <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-purple-900 to-purple-600
                            bg-clip-text text-transparent">Our Key Initiatives</h2>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <ProgramCard
                                image="/images/cancer-survivors.jpg"
                                title="ABCSC Initiative"
                                description="Supporting cancer survivors and their families since 2020 through comprehensive care and community support. A place where no one fights alone."
                            />
                            <ProgramCard
                                image="/images/youth-empowerment.jpg"
                                title="Youth Empowerment (ABYETS)"
                                description="Nurturing young entrepreneurs ages 8-17 through comprehensive training, mentorship, and hands-on business experience."
                            />
                        </div>
                    </div>

                    {/* Monthly Outreach */}
                    <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-purple-100/20">
                        <div className="max-w-3xl mx-auto">
                            <div className="flex items-center justify-center mb-8 space-x-4">
                                <div className="h-px w-12 bg-gradient-to-r from-transparent to-purple-300"></div>
                                <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-900 to-purple-600
                                             bg-clip-text text-transparent">Community Outreach</h2>
                                <div className="h-px w-12 bg-gradient-to-l from-transparent to-purple-300"></div>
                            </div>
                            <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                                <p className="leading-relaxed">
                                    There are many in Antigua and Barbuda who need assistance financially, socially, and
                                    spiritually, and we believe that it is our responsibility to help our fellow citizens.
                                    As guided by Proverbs 19:17, "He that hath pity on the poor lendeth unto the Lord; and that which he hath given will he pay him again."
                                </p>
                                <p className="leading-relaxed">
                                    CCM has chosen not to turn a blind eye. Since 2008, we have been having and continue
                                    to have our monthly outreach of food and clothing distribution to needy families.
                                    All of our services are free, and we operate through fundraisers, donations and
                                    volunteerism.
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                    <div className="bg-purple-50 p-6 rounded-xl">
                                        <h3 className="text-xl font-semibold text-purple-900 mb-4">Our Programs</h3>
                                        <ul className="space-y-3">
                                            {[
                                                "Monthly food and clothing distribution",
                                                "Financial assistance for medical needs",
                                                "Counseling and emotional support",
                                                "Cancer survivor support through ABCSC",
                                                "Youth entrepreneurship training (ABYETS)",
                                                "Annual back-to-school support program"
                                            ].map((item, index) => (
                                                <li key={index} className="flex items-start">
                                                    <ChevronRight className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-1" />
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="bg-purple-50 p-6 rounded-xl">
                                        <h3 className="text-xl font-semibold text-purple-900 mb-4">Get Involved</h3>
                                        <p className="mb-6">We welcome your suggestions and ideas for the continued development
                                            of our ministry. For more information about our organization and how you can support us,
                                            please contact:</p>
                                        <ul className="space-y-3 mb-6">
                                            <li className="flex items-center space-x-2">
                                                <span className="font-semibold">Emelda Benjamin:</span>
                                                <span>268 722 1019</span>
                                            </li>
                                            <li className="flex items-center space-x-2">
                                                <span className="font-semibold">Wilda JnoBaptist:</span>
                                                <span>268 720 5695</span>
                                            </li>
                                        </ul>
                                        <div className="flex items-center space-x-2 pt-4 border-t border-purple-200">
                                            <Book className="h-4 w-4 text-purple-600" />
                                            <p className="text-sm text-gray-600">
                                                Support our ministry with our inspirational poetry books, available for $35.00
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-purple-50/50 to-white" />
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
                    <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-purple-900 to-purple-600
                      bg-clip-text text-transparent">
                        Join Our Community
                    </h2>
                    <p className="text-lg text-gray-600 mb-8">
                        Together, we can make a lasting impact in our community. Every contribution makes a difference.
                    </p>
                    <Link to="/contact"
                          className="group inline-flex items-center px-8 py-4 rounded-xl bg-purple-600
                        text-white font-semibold text-lg shadow-lg hover:bg-purple-700
                        transition-all duration-300 transform hover:-translate-y-1
                        hover:shadow-xl border border-purple-500 hover:border-purple-600
                        relative overflow-hidden">
                        <span className="relative z-10 flex items-center">
                            Get Involved
                            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1
                                           transition-transform duration-300" />
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-500
                         group-hover:scale-110 transition-transform duration-500" />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;